import * as React from "react";
import { useSpring, animated } from "@react-spring/web";
import { FormField, useInjector } from "@bb-vendure/admin-ui/react";
import {
  Add,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  MoreVert,
  Search,
} from "@mui/icons-material";
import { debounce } from "lodash";
import { Menu, MenuItem, TableCell, TableRow } from "@mui/material";
import { I18nService } from "@bb-vendure/admin-ui/core";

interface SearchProps {
  searchTerm: (term: string) => void;
  span: number;
}

const TableSearch: React.FC<SearchProps> = ({ searchTerm, span = 6 }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [value, setValue] = React.useState("");
  const animation = useSpring({
    paddingTop: isOpen ? "32px" : "0px",
    paddingBottom: isOpen ? "32px" : "0px",
    height: isOpen ? "140px" : "0px",
    config: { duration: 150 },
    background: "#f2f2f2",
  });
  const handleClickSearch = () => setIsOpen(!isOpen);
  const debouncedSearch = debounce((value) => {
    setValue(value);
  }, 300);

  React.useEffect(() => {
    searchTerm(value);
  }, [value]);

  const SearchIcon = ({ color = "black", size = "36px" }) => (
    <svg
      version="1.1"
      viewBox="0 0 36 36"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      focusable="false"
      role="img"
      style={{ fill: color, width: size, height: "auto" }}
    >
      <path
        className="clr-i-outline clr-i-outline-path-1"
        d="M16.33,5.05A10.95,10.95,0,1,1,5.39,16,11,11,0,0,1,16.33,5.05m0-2.05a13,13,0,1,0,13,13,13,13,0,0,0-13-13Z"
      ></path>
      <path
        className="clr-i-outline clr-i-outline-path-2"
        d="M35,33.29l-7.37-7.42-1.42,1.41,7.37,7.42A1,1,0,1,0,35,33.29Z"
      ></path>
    </svg>
  );

  return (
    <>
      <TableRow style={{ position: "relative" }}>
        <button
          onClick={handleClickSearch}
          className="button-ghost"
          style={{
            position: "absolute",
            top: "-10px",
            left: "5px",

            background: isOpen ? "#0283b2" : "white",
          }}
        >
          <SearchIcon color={isOpen ? "white" : "#000000"} size="16px" />
        </button>
      </TableRow>
      <TableRow>
        <TableCell colSpan={span} padding="none">
          <div style={{ width: "100%" }}>
            <animated.div
              style={{
                ...animation,
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                paddingLeft: "50px",
                paddingRight: "20px",
                overflow: "hidden",
              }}
            >
              <FormField>
                <input
                  style={{ marginBottom: "8px" }}
                  placeholder="Filter by name"
                  onChange={(e) => debouncedSearch(e.target.value)}
                />
              </FormField>
              <FormField>
                <button
                  className="button-ghost"
                  style={{
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    background: "#e6e5e5",
                    marginTop: "8px",
                  }}
                >
                  <Add style={{ fontSize: 14 }} />
                  Add Filter
                  <span
                    style={{
                      border: "1px solid lightgray",
                      background: "#f2f2f2",
                      borderRadius: "2px",
                      padding: "0px 2px 0px 2px",
                      color: "#8c8c8c",
                    }}
                  >
                    F
                  </span>
                  <MoreVert style={{ fontSize: 14 }} />
                </button>
              </FormField>
            </animated.div>
          </div>
        </TableCell>
      </TableRow>
    </>
  );
};

interface TableItemsPerPageButtonProps {
  take: number;
  setTake: (take: number) => void;
}

const TableItemsPerPageButton: React.FC<TableItemsPerPageButtonProps> = ({
  take,
  setTake,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const onSelectTake = (v: number) => {
    setTake(v);
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const translator = useInjector(I18nService);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget as HTMLButtonElement);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <button
        className="button secondary"
        style={{ gap: "none", marginLeft: 20 }}
        onClick={handleClick}
      >
        {translator.translate("common.items-per-page-option", {
          count: take,
        })}

        <MoreVert style={{ fontSize: 18 }} />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          style={{ fontSize: "0.7rem", marginLeft: 8 }}
          onClick={() => {
            onSelectTake(10);
          }}
        >
          {translator.translate("common.items-per-page-option", {
            count: 10,
          })}
        </MenuItem>
        <MenuItem
          style={{ fontSize: "0.7rem", marginLeft: 8 }}
          onClick={() => {
            onSelectTake(25);
          }}
        >
          {translator.translate("common.items-per-page-option", {
            count: 25,
          })}
        </MenuItem>
        <MenuItem
          style={{ fontSize: "0.7rem", marginLeft: 8 }}
          onClick={() => {
            onSelectTake(50);
          }}
        >
          {translator.translate("common.items-per-page-option", {
            count: 50,
          })}
        </MenuItem>
        <MenuItem
          style={{ fontSize: "0.7rem", marginLeft: 8 }}
          onClick={() => {
            onSelectTake(100);
          }}
        >
          {translator.translate("common.items-per-page-option", {
            count: 100,
          })}
        </MenuItem>
      </Menu>
    </div>
  );
};
const EmptyIcon = ({
  color = "#97979a",
  size = "36px",
  span = 6,
  label = "No results",
}) => (
  <TableCell colSpan={span}>
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <svg
        version="1.1"
        viewBox="0 0 36 36"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        focusable="false"
        role="img"
        style={{ fill: color, width: size, height: "auto" }}
      >
        <path
          className="clr-i-outline clr-i-outline-path-1"
          d="M18,2.5c-8.82,0-16,6.28-16,14s7.18,14,16,14a18,18,0,0,0,4.88-.68l5.53,3.52a1,1,0,0,0,1.54-.84l0-6.73a13,13,0,0,0,4-9.27C34,8.78,26.82,2.5,18,2.5ZM28.29,24.61a1,1,0,0,0-.32.73l0,5.34-4.38-2.79a1,1,0,0,0-.83-.11A16,16,0,0,1,18,28.5c-7.72,0-14-5.38-14-12s6.28-12,14-12,14,5.38,14,12A11.08,11.08,0,0,1,28.29,24.61Z"
        ></path>
        <path
          className="clr-i-solid clr-i-solid-path-1"
          d="M18,2.5c-8.82,0-16,6.28-16,14s7.18,14,16,14a18,18,0,0,0,4.88-.68l5.53,3.52a1,1,0,0,0,1.54-.84l0-6.73a13,13,0,0,0,4-9.27C34,8.78,26.82,2.5,18,2.5ZM16.93,9.13a1.41,1.41,0,1,1,2.81,0V18.9a1.41,1.41,0,1,1-2.81,0Zm1.41,17.35a1.87,1.87,0,1,1,1.87-1.87A1.87,1.87,0,0,1,18.34,26.47Z"
        ></path>
      </svg>
      {label}
    </div>
  </TableCell>
);

const TableItemCount: React.FC<{
  take: number;
  skip: number;
  total: number;
}> = ({ take, skip, total }) => {
  const totalCalc = total < skip + take ? total : skip + take;
  const takeCalc = skip + take > total ? total : skip + take;
  return (
    <a>
      {skip} - {takeCalc} of {total}
    </a>
  );
};

const TablePagination: React.FC<{
  take: number;
  skip: number;
  total: number;
  prevPage: () => void;
  nextPage: () => void;
  toPage: (page: number) => void;
}> = ({ take, skip, total, prevPage, nextPage, toPage }) => {
  const totalPages = Math.ceil(total / take);

  const currentPage = Math.floor(skip / take) + 1;
  const pages = new Array<number>(totalPages).fill(0);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginRight: "20px",
      }}
    >
      <button
        className="button secondary"
        onClick={() => {
          if (currentPage > 1) {
            prevPage();
          }
        }}
      >
        <KeyboardDoubleArrowLeft style={{ fontSize: 16 }} />
      </button>
      {pages.fill(0).map((_, i) => (
        <button
          key={i}
          style={{
            border: "none",
            background: "#fafafa",
            transition: "background-color 0.3s ease",
            height: "32px",
            width: "28px",
            marginLeft: "2px",
            marginRight: "2px",
            borderBottom: currentPage === i + 1 ? "2px solid #1cc2ff" : "none",
            outline: "none",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f2f2f2")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#fafafa")
          }
          onClick={() => {
            toPage(i + 1);
          }}
        >
          {i + 1}
        </button>
      ))}
      <button
        className="button secondary"
        onClick={() => {
          if (currentPage < totalPages) {
            nextPage();
          }
        }}
      >
        <KeyboardDoubleArrowRight style={{ fontSize: 16 }} />
      </button>
    </div>
  );
};

const TablePaginationComponent: React.FC<{
  pagination: any;
  setPagination: (pagination: any) => void;
  total: number;
}> = ({ pagination, setPagination, total }) => {
  return (
    <div
      style={{
        marginTop: 10,
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <TableItemsPerPageButton
        take={pagination.take}
        setTake={(t) => setPagination({ ...pagination, take: t })}
      />
      <TableItemCount
        take={pagination.take}
        skip={pagination.skip}
        total={total}
      />
      <TablePagination
        take={pagination.take}
        skip={pagination.skip}
        total={total}
        prevPage={() => {
          setPagination({
            ...pagination,
            skip: pagination.skip - pagination.take,
          });
        }}
        nextPage={() => {
          setPagination({
            ...pagination,
            skip: pagination.skip + pagination.take,
          });
        }}
        toPage={(page) => {
          setPagination({
            ...pagination,
            skip: page * pagination.take - pagination.take,
          });
        }}
      />
    </div>
  );
};

export {
  TableSearch,
  TableItemsPerPageButton,
  TableItemCount,
  TablePagination,
  EmptyIcon,
  TablePaginationComponent,
};
